import React, { useEffect, useMemo, useState } from "react";
import { FaProjectDiagram, FaRocket } from "react-icons/fa";
import "./LeadershipDesign2Modern.css";

const MA_APPLY_URL = "https://admissions.eusbd.eu/esbd-apply/MA-GBSL/";

const decodeHtmlEntities = (value) => {
  if (typeof value !== "string") return value;
  if (typeof document === "undefined") {
    return value
      .replaceAll("&amp;", "&")
      .replaceAll("&quot;", '"')
      .replaceAll("&apos;", "'")
      .replaceAll("&lt;", "<")
      .replaceAll("&gt;", ">");
  }

  const txt = document.createElement("textarea");
  txt.innerHTML = value;
  return txt.value;
};

const splitParagraphs = (text) => {
  if (!text || typeof text !== "string") return [];
  return text
    .split("\n")
    .map((p) => p.trim())
    .filter(Boolean);
};

const LeadershipDesign2Modern = ({ mastersprogram }) => {
  const [activeId, setActiveId] = useState("overview");
  const [openModules, setOpenModules] = useState({});
  const [openEntryAcc, setOpenEntryAcc] = useState({});

  const sections = useMemo(
    () => [
      { id: "overview", label: "Overview" },
      { id: "outcomes", label: "Learning Outcomes" },
      { id: "modules", label: "Modules" },
      { id: "entry", label: "Entry Requirements" },
      { id: "fees", label: "Programme Fees" },
      { id: "how-to-pay", label: "How To Pay" },
      { id: "scholarships", label: "Scholarships" },
      { id: "careers", label: "Career Opportunities" },
    ],
    [],
  );

  const byTopItemTitle = (title) =>
    mastersprogram?.topItems?.find((x) => x?.title === title)?.description;

  const heroTitleItalic = useMemo(() => {
    const raw = mastersprogram?.title || "";
    const prefix = "Master of Arts in ";
    if (raw.startsWith(prefix)) return raw.slice(prefix.length);
    return raw;
  }, [mastersprogram?.title]);

  useEffect(() => {
    const onScroll = () => {
      const st = window.scrollY + 160;
      const ids = sections.map((s) => s.id);
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.offsetTop <= st && el.offsetTop + el.offsetHeight > st) {
          setActiveId(id);
          break;
        }
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [sections]);

  const toggleOpenModule = (key) => {
    setOpenModules((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleOpenEntry = (key) => {
    setOpenEntryAcc((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const scrollToSection = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const overviewParagraphs = splitParagraphs(mastersprogram?.overview);
  const learningOutcomesStructured = mastersprogram?.learningOutcomesStructured;
  const learningOutcomeCards = mastersprogram?.learningOutcomes || [];

  // "Programme Overview" highlight cards (screenshot/guide ke مطابق)
  const overviewHighlights = useMemo(
    () => [
      {
        title: "100% Online MA",
        text: "Complete your Master's degree entirely online, from anywhere in the world",
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      },
      {
        title: "Licensed in Malta, Europe",
        text: "By the Malta Further & Higher Education Authority (MFHEA)",
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11m16-11v11"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      },
      {
        title: "Recognised Internationally",
        text: "Accredited European higher education institution awarding university-level degrees",
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="white"
              strokeWidth="1.5"
            />
            <path
              d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"
              stroke="white"
              strokeWidth="1.5"
            />
          </svg>
        ),
      },
      {
        title: "Affordable Tuition",
        text: "€6,000 total fees with option to pay in instalments",
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        ),
      },
      {
        title: "Scholarships Available",
        text: "Multiple scholarship programmes for students across Africa, Asia, and Europe",
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
              stroke="white"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
          </svg>
        ),
      },
      {
        title: "European Summer School Programme",
        text: "Optional immersive programme hosted by our partner institutions across Europe",
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        ),
      },
      {
        title: "Work Experience",
        text: "Practical work experience opportunities integrated into the programme",
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        ),
      },
      {
        title: "Study Tour",
        text: "International study tour experience as part of your academic journey",
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              stroke="white"
              strokeWidth="1.5"
            />
            <path
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              stroke="white"
              strokeWidth="1.5"
            />
          </svg>
        ),
      },
    ],
    [],
  );

  // Preview fees for leadership: Registration 150, Enrolment 195, PG Cert 1600, PG Dip 2600, MA 3400 = 7945
  const feesOverride = mastersprogram?.id === "leadership" ? [
    { title: "Registration Fee", amount: 150 },
    { title: "Enrolment Fee", amount: 195 },
    { title: "Postgraduate Certificate", amount: 1320 },
    { title: "Postgraduate Diploma", amount: 2140 },
    { title: "MA Global Business & Strategic Leadership", amount: 2795 },
  ] : null;
  const fees = feesOverride || mastersprogram?.fees || [];
  const totalFees = fees.reduce((acc, f) => acc + Number(f?.amount || 0), 0);

  const scholarships = mastersprogram?.scholarships || [];
  const careerOpportunities = mastersprogram?.careerOpportunities || [];

  // Preview modules for leadership (ma-programme-page-final)
  const previewModules = mastersprogram?.id === "leadership" ? {
    semester1: { title: "Foundation", courses: [
      { title: "Global Strategic Management", credits: 6, description: "This module introduces students to advanced strategic management concepts in a global context." },
      { title: "Leadership & Organisational Development in Global Business", credits: 9, description: "This module explores key leadership theories and their practical application in modern organisations." },
      { title: "Business Ethics, Governance & Strategic Risk", credits: 9, description: "This module explores the critical intersection of business ethics, corporate governance, and strategic risk." },
      { title: "Strategic Marketing in Global Business Contexts", credits: 6, description: "This module explores global marketing strategy and how firms build brand value across international markets." },
    ]},
    semester2: { title: "Strategic Development", courses: [
      { title: "Innovation, Entrepreneurship & Human Resource", credits: 9, description: "This module equips students with strategic insights into innovation, entrepreneurship, and human resource development." },
      { title: "Financial Decision-Making in Global Business", credits: 6, description: "This module equips students with financial tools to support strategic decision-making in international organisations." },
      { title: "International Business & Global Strategy", credits: 6, description: "This module explores strategic challenges organisations face in the global business environment." },
      { title: "Research Methods for Strategic Leadership", credits: 9, description: "This module develops learners' ability to critically design, implement, and evaluate applied research." },
    ]},
    semester3: { title: "Capstone & Dissertation", courses: [
      { title: "Dissertation: Applied Strategic Leadership Research", credits: 30, description: "This capstone module enables learners to demonstrate mastery through an independent Master's thesis." },
    ]},
  } : null;

  const modulesData = previewModules || mastersprogram?.modules || {};
  // Our data modules structure is `modules: { semester1: { title, courses: [] } }`
  const semesterBlocks = [
    {
      key: "semester1",
      headBg: "var(--navy)",
      fallbackHeadBg: "var(--navy)",
      title: modulesData?.semester1?.title,
      courses: modulesData?.semester1?.courses || [],
      iconTone: "a",
    },
    {
      key: "semester2",
      headBg: "var(--navy-mid)",
      fallbackHeadBg: "var(--navy-mid)",
      title: modulesData?.semester2?.title,
      courses: modulesData?.semester2?.courses || [],
      iconTone: "b",
    },
    {
      key: "semester3",
      headBg: "#1a4a7a",
      fallbackHeadBg: "#1a4a7a",
      title: modulesData?.semester3?.title,
      courses: modulesData?.semester3?.courses || [],
      iconTone: "c",
    },
  ].filter((s) => s.title && s.courses && s.courses.length > 0);

  const careerIconsSvg = {
    globe: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" stroke="white" strokeWidth="1.5"/></svg>,
    bolt: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="white" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
    bulb: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>,
    star: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="white" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
    chart: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M16 8v8m-4-5v5m-4-2v2m-2 4h16a2 2 0 002-2V5a2 2 0 00-2-2H6a2 2 0 00-2 2v14a2 2 0 002 2z" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>,
    cap: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422A12.083 12.083 0 0121 12.083V17l-9 5-9-5v-4.917c0-1.03.13-2.03.379-2.99L12 14z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    chat: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    people: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    govt: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11m16-11v11M8 14v3m4-3v3m4-3v3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    office: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3m4-11h2m4 0h2m-6 4h2m4 0h2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  };

  const previewCareers = mastersprogram?.id === "leadership" ? [
    { description: "Global Strategy Manager", icon: "globe" },
    { description: "International Business Development Manager", icon: "bolt" },
    { description: "Strategic Consultant", icon: "bulb" },
    { description: "Head of Innovation", icon: "star" },
    { description: "Marketing Director", icon: "chart" },
    { description: "Political Affairs Analyst", icon: "cap" },
    { description: "Corporate Line & Communications Director", icon: "chat" },
    { description: "Leadership & Organisational Development Specialist", icon: "people" },
    { description: "Public Sector Leadership & Policy Manager", icon: "govt" },
    { description: "Chief Operating Officer", icon: "office" },
  ] : null;

  const careersToShow = previewCareers || careerOpportunities;

  const renderCareerIcon = (career) => {
    const iconSrc = career?.icon;
    if (!iconSrc) return null;

    if (typeof iconSrc === "string" && careerIconsSvg[iconSrc]) {
      return careerIconsSvg[iconSrc];
    }
    const base = String(iconSrc).split("/").pop();
    if (base === "accelerate-svgrepo-com.svg") {
      return <FaRocket size={22} color="white" aria-hidden="true" />;
    }
    if (base === "network-svgrepo-com.svg") {
      return <FaProjectDiagram size={22} color="white" aria-hidden="true" />;
    }

    return (
      <img
        src={iconSrc}
        alt=""
        className="career-card__img"
        loading="lazy"
      />
    );
  };

  return (
    <div className="lead2m-page">
      <section className="hero" aria-label="Programme hero">
        <div className="hero__bg" aria-hidden="true">
          <div
            className="hero__bg-img"
            style={{
              backgroundImage: `url(${mastersprogram?.topBgImage || ""})`,
            }}
          />
          <div className="hero__bg-ov" />
        </div>

        <div className="hero__inner">
          <div className="hero__left">
            <div className="hero__badge">Flagship Degree</div>
            <h1 className="hero__title">
              Master of Arts in <em>{heroTitleItalic}</em>
            </h1>
            <p className="hero__desc">{decodeHtmlEntities(mastersprogram?.blab?.finish)}</p>

            <div className="hero__btns">
              <a className="hero__btn hero__btn--gold" href={MA_APPLY_URL} target="_blank" rel="noreferrer">
                Apply Now
              </a>
              {mastersprogram?.brochurelink ? (
                <a
                  className="hero__btn hero__btn--ghost"
                  href={mastersprogram.brochurelink}
                  target="_blank"
                  rel="noreferrer"
                >
                  Download Brochure
                </a>
              ) : (
                <a className="hero__btn hero__btn--ghost" href="#">
                  Download Brochure
                </a>
              )}
            </div>
          </div>

          <div className="hero__details" aria-label="Programme details">
            <div className="hero__det">
              <div className="hero__det-label">Duration</div>
              <div className="hero__det-value">Full Time - 15 Month</div>
              <div className="hero__det-value">Part Time - 24 Month</div>
            </div>
            <div className="hero__det">
              <div className="hero__det-label">Type</div>
              <div className="hero__det-value">{decodeHtmlEntities(byTopItemTitle("Type of Qualification") || "N/A")}</div>
            </div>
            <div className="hero__det">
              <div className="hero__det-label">Format</div>
              <div className="hero__det-value">{decodeHtmlEntities(byTopItemTitle("Format") || "N/A")}</div>
            </div>
            <div className="hero__det">
              <div className="hero__det-label">Credits</div>
              <div className="hero__det-value">{decodeHtmlEntities(byTopItemTitle("Postgraduate Credits") || "N/A")}</div>
            </div>
            <div className="hero__det">
              <div className="hero__det-label">Level</div>
              <div className="hero__det-value">{decodeHtmlEntities(byTopItemTitle("Level of Qualification") || "N/A")}</div>
            </div>
            <div className="hero__det">
              <div className="hero__det-label">Intakes</div>
                <div className="hero__det-value"> March 2027</div>
                <div className="hero__det-value">September 2027</div>
               
              {/* <div className="hero__det-value">

                {decodeHtmlEntities(byTopItemTitle("Intake 1") || byTopItemTitle("Intake 2") || "N/A")}
              </div> */}
            </div>
            <div className="hero__det">
              <div className="hero__det-label">Language</div>
              <div className="hero__det-value">{decodeHtmlEntities(byTopItemTitle("Languages") || "N/A")}</div>
            </div>
            <div className="hero__det">
              <div className="hero__det-label">Location</div>
              <div className="hero__det-value">Malta (Online)</div>
            </div>
          </div>
        </div>
      </section>

      <div className="anchor">
        <div className="anchor__inner">
          {sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className={activeId === s.id ? "active" : undefined}
              onClick={(e) => scrollToSection(e, s.id)}
            >
              {s.label}
            </a>
          ))}
        </div>
      </div>

      <div className="section" id="overview">
        <div className="overview-grid">
          <div className="overview-text">
            <h2>Programme Overview</h2>
            {overviewParagraphs.map((p, idx) => (
              <p key={idx}>{decodeHtmlEntities(p)}</p>
            ))}
          </div>

          <div className="overview-highlights" aria-label="Programme highlights">
            {overviewHighlights.map((h, idx) => (
              <div key={`${h.title}-${idx}`} className="overview-hl">
                <div className="overview-hl__icon">{h.icon}</div>
                <div>
                  <div className="overview-hl__title">{h.title}</div>
                  <div className="overview-hl__text">{h.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="section section--alt" id="outcomes">
        <div className="section__inner">
          <div className="section__header">
            <h2>Programme Learning Outcomes</h2>
            <p>Upon completion, graduates will have developed competences, knowledge, and skills across the following areas:</p>
          </div>

          {learningOutcomesStructured ? (
            <>
              <h3 className="outcome-subtitle">Competences</h3>
              <div className="outcome-grid">
                {learningOutcomesStructured.competences?.map((text, idx) => (
                  <div key={`c-${idx}`} className="outcome-card">
                    <div className="outcome-card__icon">{String.fromCharCode(97 + idx)}</div>
                    <div className="outcome-card__text">{decodeHtmlEntities(text)}</div>
                  </div>
                ))}
              </div>
              <h3 className="outcome-subtitle">Knowledge</h3>
              <div className="outcome-grid">
                {learningOutcomesStructured.knowledge?.map((text, idx) => (
                  <div key={`k-${idx}`} className="outcome-card">
                    <div className="outcome-card__icon">{String.fromCharCode(97 + idx)}</div>
                    <div className="outcome-card__text">{decodeHtmlEntities(text)}</div>
                  </div>
                ))}
              </div>
              <h3 className="outcome-subtitle">Skills</h3>
              <div className="outcome-grid">
                {learningOutcomesStructured.skills?.map((text, idx) => (
                  <div key={`s-${idx}`} className="outcome-card">
                    <div className="outcome-card__icon">{String.fromCharCode(97 + idx)}</div>
                    <div className="outcome-card__text">{decodeHtmlEntities(text)}</div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="outcome-grid" aria-label="Learning outcomes cards">
              {learningOutcomeCards.slice(0, 12).map((outcome, idx) => (
                <div key={`${outcome}-${idx}`} className="outcome-card">
                  <div className="outcome-card__icon">{String.fromCharCode(97 + idx)}</div>
                  <div className="outcome-card__text">{decodeHtmlEntities(outcome)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="section" id="modules">
        <div className="section__header">
          <h2>Programme Modules</h2>
          <p>8 taught modules across three semesters, plus a Master&apos;s Thesis</p>
        </div>

        <div className="mod-semesters">
          {semesterBlocks.map((sem, semIdx) => {
            const totalECTS = sem.courses.reduce((sum, c) => sum + Number(c?.credits || 0), 0);
            const moduleCount = sem.courses.length;
            const headStyle = { background: sem.headBg || sem.fallbackHeadBg };

            return (
              <div key={sem.key} className="mod-sem">
                <div className="mod-sem__head" style={headStyle}>
                  <h3>{sem.title}</h3>
                  <span>
                    {moduleCount} Modules | {totalECTS} ECTS
                  </span>
                </div>

                <div className="mod-sem__list">
                  {sem.courses.map((mod, modIndex) => {
                    const key = `${sem.key}-${modIndex}`;
                    const isOpen = Boolean(openModules[key]);
                    return (
                      <div key={key} className={`mod-item ${isOpen ? "open" : ""}`}>
                        <div
                          className="mod-item__top"
                          role="button"
                          tabIndex={0}
                          onClick={() => toggleOpenModule(key)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") toggleOpenModule(key);
                          }}
                        >
                          <div className="mod-item__name">{decodeHtmlEntities(mod?.title)}</div>
                          <div className="mod-item__ects">{Number(mod?.credits || 0)} ECTS</div>
                          <div className="mod-item__toggle" aria-hidden="true">
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                              <path
                                d="M4 6l4 4 4-4"
                                stroke="#999"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>
                        <div className="mod-item__desc">
                          <p>{decodeHtmlEntities(mod?.description)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="section section--alt" id="entry">
        <div className="section__inner">
          <div className="section__header">
            <h2>Entry Requirements</h2>
          </div>

          <div className="entry-acc-list">
            {((mastersprogram?.id === "leadership" ? [
              { title: "Academic Entry Requirement", description: "Applicants must hold a recognised MQF Level 6 qualification (Bachelor's degree or equivalent) in Business, Management, Economics, International Relations, Public Policy, Social Sciences, or another relevant discipline that provides appropriate preparation for postgraduate study." },
              { title: "English Language Requirement", description: "Applicants must demonstrate proficiency in English through one of the following: IELTS Academic overall score of 6.0 with no individual component below 5.5, or an equivalent English language qualification accepted by the institution." },
              { title: "Digital Literacy Requirement (Online Learning Readiness)", description: "As the programme is delivered fully online, applicants must demonstrate basic digital literacy, including the ability to: use email and online communication tools professionally; access and navigate the LearnDash learning platform; use standard productivity software for written assignments; upload and download documents securely; join and participate in live online sessions using Microsoft Teams or Zoom; and conduct basic online research using credible sources." },
              { title: "Entry Interview / Online Readiness Check", description: "Where required, the institution may conduct a brief online readiness check (short interview or written application statement) to confirm the applicant's ability to participate effectively in a fully online postgraduate learning environment." },
            ] : null) || mastersprogram?.eligibilityRequirements || []).map((req, idx) => {
              const key = `req-${idx}`;
              const isOpen = Boolean(openEntryAcc[key]);
              return (
                <div key={key} className={`entry-acc ${isOpen ? "open" : ""}`}>
                  <div
                    className="entry-acc__head"
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleOpenEntry(key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") toggleOpenEntry(key);
                    }}
                  >
                    <span className="entry-acc__title">{decodeHtmlEntities(req?.title)}</span>
                    <span className="entry-acc__icon" aria-hidden="true">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M4 6l4 4 4-4"
                          stroke="#0C1F3F"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                  <div className="entry-acc__body">
                    <div className="entry-acc__content">{decodeHtmlEntities(req?.description)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="section" id="fees">
        <div className="section__header">
          <h2>Programme Fees</h2>
          <p>The fees below relate to new students registering for the 2026-2027 session.</p>
        </div>

        <table className="fees-table">
          <thead>
            <tr>
              <th>Fee Description</th>
              <th>Amount (EUR)</th>
            </tr>
          </thead>
          <tbody>
            {fees.map((f, idx) => (
              <tr key={`fee-${idx}`}>
                <td>{decodeHtmlEntities(f?.title)}</td>
                <td>&euro;{Number(f?.amount || 0).toLocaleString()}</td>
              </tr>
            ))}
            <tr>
              <td>
                <strong>Total Fees</strong>
              </td>
              <td>
                <strong>&euro;{Number(totalFees || 0).toLocaleString()}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="section section--navy how-to-pay-section" id="how-to-pay">
        <div className="section__inner">
          <div className="section__header">
            <h2>How To Pay</h2>
            <p>You can pay your fees in a number of ways, including online payment via Stripe, PayPal, Western Union, or bank transfer</p>
          </div>
          <div className="how-to-pay-grid">
            <div className="how-to-pay-card how-to-pay-card--featured">
              <span className="how-to-pay-card__badge">Best Choice</span>
              <div className="how-to-pay-card__inner">
                <h3>One-Time Payment</h3>
                <div className="how-to-pay-card__body">
                  <div className="how-to-pay-card__price">&euro;6,000</div>
                  <div className="how-to-pay-card__old">&euro;6,600</div>
                  <p>Pay the full programme fee upfront and receive exclusive access to a leadership webinar with an international expert.</p>
                  <span className="how-to-pay-card__save">SAVE &euro;600</span>
                  <p className="how-to-pay-card__tagline">Pay once &amp; save!</p>
                </div>
              </div>
            </div>
            <div className="how-to-pay-card">
              <span className="how-to-pay-card__badge how-to-pay-card__badge--alt">Flexible</span>
              <div className="how-to-pay-card__inner how-to-pay-card__inner--alt">
                <h3>Instalment Plan</h3>
                <div className="how-to-pay-card__body">
                  <div className="how-to-pay-card__price">
                    &euro;2,400
                    <span>1st instalment</span>
                  </div>
                  <p className="how-to-pay-card__detail">(3 Instalments)</p>
                  <p className="how-to-pay-card__instalment-breakdown">1st: &euro;2,400 &bull; 2nd: &euro;2,100 &bull; 3rd: &euro;2,100</p>
                  <p className="how-to-pay-card__total">Total: &euro;6,600</p>
                  <div className="how-to-pay-instalments">
                    {["1st", "2nd", "3rd"].map((n) => (
                      <span key={n}>{n}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="section section--navy" id="scholarships">
        <div className="section__inner">
          <div className="section__header">
            <h2>Scholarships</h2>
            <p>ESBD offers a range of scholarships to support access and recognise excellence.</p>
          </div>

          <div className="schol-grid">
            {(mastersprogram?.id === "leadership" ? [
              { title: "ED Africa Scholarship", text: "For African students showing leadership potential" },
              { title: "ED Asia Scholarship", text: "For Asian students demonstrating academic excellence" },
              { title: "ED Europe Scholarship", text: "For European students pursuing business and diplomacy" },
              { title: "ED Without Borders Scholarship", text: "For students from conflict or disadvantaged regions" },
              { title: "ED Leadership Excellence Scholarship", text: "For outstanding leadership in community work" },
            ] : scholarships).map((s, idx) => (
              <div key={`sch-${idx}`} className="schol-card">
                <h4 className="schol-card__title">{decodeHtmlEntities(s?.title)}</h4>
                <p className="schol-card__text">
                  {decodeHtmlEntities(s?.text || s?.status || "")}
                  {!s?.text && s?.value ? ` • ${s.value}% fee reduction` : ""}
                </p>
                <a href="/register-your-interest">Apply for Scholarship</a>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="section" id="careers">
        <div className="section__header">
          <h2>Career Opportunities</h2>
          <p>Graduates are prepared for leadership roles across global organisations.</p>
        </div>

        <div className="career-grid">
          {careersToShow.map((c, idx) => (
            <div key={`car-${idx}`} className="career-card">
              <div className="career-card__icon">
                {renderCareerIcon(c) || (
                  <span className="career-card__img-fallback" aria-hidden="true" />
                )}
              </div>
              <div className="career-card__title">{decodeHtmlEntities(c?.description)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="cta-section">
        <h2>Ready to Transform Your Career?</h2>
        <p>
          Apply now for the {decodeHtmlEntities(byTopItemTitle("Intake 1") || "next intake")}.
        </p>
        <a href={MA_APPLY_URL} target="_blank" rel="noreferrer">
          Apply Now
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M4 12L12 4M12 4H6M12 4v6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      </div>
    </div>
  );
};

export default LeadershipDesign2Modern;

